import { useState } from 'react';
import { DIRECT_DISCOUNT_PCT } from '../lib/property-schema.js';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import Section from '../components/Section.jsx';
import Seo from '../components/Seo.jsx';

// The human half of the MCP feature: the endpoint itself is /mcp, this page
// tells a person what it is and how to plug it in. Host-neutral like all
// chrome: the tools surface homes through the index, this page names none.

const MCP_URL = 'https://charleston.vacations/mcp';

const EXAMPLE_PROMPTS = [
  'Is anything bookable in Charleston the second week of October? What would four nights cost all-in?',
  "We land Friday around 4. What's the weather doing this weekend, and what should we not miss?",
  'Compare staying in Mount Pleasant with staying downtown for a family of five.',
  'Find me an open long weekend in November and price it.'
];

function EndpointCard() {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Selection still works; the button is a convenience.
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-navy-100 bg-cream-100/60 px-5 py-4">
      <code className="select-all font-mono text-sm text-navy-900">{MCP_URL}</code>
      <button
        type="button"
        onClick={copy}
        className="rounded-lg bg-navy-900 px-3 py-1.5 text-xs font-medium text-cream-50 hover:bg-navy-800"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function Ai() {
  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title="Connect Your AI · Charleston.Vacations"
        description="Charleston.Vacations is a native MCP server. Connect it to Claude, ChatGPT, or any MCP client and your AI can check live availability, price real dates, and plan your Charleston trip in one conversation."
      />
      <Header />
      <div className="mt-8 border-b border-navy-100 pb-6">
        <h1 className="font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
          Bring your AI along
        </h1>
        <p className="mt-3 max-w-2xl leading-relaxed text-navy-700">
          This site is also an MCP server. Connect it once and your AI can read today's
          brief, read our guides, check the live booking calendar, and price real dates,
          right inside the conversation you're already having.
        </p>
      </div>

      <Section eyebrow="Connect it" title="One URL, no account, no key">
        <div className="space-y-4 leading-relaxed">
          <EndpointCard />
          <p>
            Paste that URL wherever your AI accepts a custom connector or MCP server. In
            Claude, that's Settings, then Connectors, then add custom connector. In
            ChatGPT, connectors live in Settings under Apps &amp; Connectors with
            developer mode enabled. Cursor, and most other MCP clients, take the same
            URL. No login, no API key, nothing to install.
          </p>
          <p className="text-sm text-navy-500">
            Connector menus move around as these apps evolve. If you don't see the
            option, search your AI's settings for "MCP" or "connectors."
          </p>
        </div>
      </Section>

      <Section eyebrow="What it can do" title="Ask, and the answer is live">
        <div className="space-y-4 leading-relaxed">
          <p>
            Once connected, your AI has seven tools: today's conditions brief, the
            planning guides in full, every home listed here, each home's fact sheet,
            a live availability check, and an itemized price quote for real dates.
            Availability comes from the same calendar Airbnb and Vrbo sync from, and
            quotes come from the hosts' booking system, so what your AI tells you is
            what the booking page will say.
          </p>
          {/* cream-100/60 with a hairline is the boxed-list surface; white is
              for inputs only (DECISIONS 2026-09-20). */}
          <ul className="space-y-2">
            {EXAMPLE_PROMPTS.map((q) => (
              <li key={q} className="rounded-lg border border-navy-100 bg-cream-100/60 px-4 py-3 text-sm text-navy-800">
                “{q}”
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section eyebrow="The fine print" title="What connecting does and doesn't do">
        <div className="space-y-4 leading-relaxed">
          <p>
            The connection is read-only. Your AI can look things up and hand you a
            booking link with your dates already priced; it cannot place a booking or
            charge anything. Payment always happens on the hosts' secure booking page,
            after you've seen the same itemized total, and no account is required at
            any step.
          </p>
          <p>
            We host a home in the area, so we profit when you book direct. The numbers
            the tools return are the numbers the booking system charges: nightly rates
            {DIRECT_DISCOUNT_PCT}% under the same dates on Airbnb, a lower cleaning fee, and no guest
            service fee. Nothing through this connector collects your personal data.
          </p>
        </div>
      </Section>

      <div className="mt-4 border-t border-navy-100 pt-6">
        <p className="text-navy-800">
          Prefer the human way in? <Link to="/" className="text-navy-900 underline">Today's brief</Link>,{' '}
          <Link to="/guides" className="text-navy-900 underline">the guides</Link>, and{' '}
          <Link to="/stay" className="text-navy-900 underline">places to stay</Link> are all one click.
        </p>
      </div>
      <Footer />
    </div>
  );
}
